// Pure formatting + sparkline geometry for the server-health widget (#284). Kept out of the
// component so it can be unit-tested.

/** Bytes as a short human string: "3.9 GB", "512 MB". Binary units (what `df`/`free` show). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  // One decimal below 10 (e.g. 3.9 GB), whole otherwise; drop a trailing ".0" (1 KB, not 1.0 KB).
  const text = v >= 10 || i === 0 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, "");
  return `${text} ${units[i]}`;
}

/** A fraction 0..1 as a whole-percent string, or "—" when null (no sample yet). */
export function formatPercent(fraction: number | null): string {
  if (fraction === null || !Number.isFinite(fraction)) return "—";
  return `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%`;
}

/** Host uptime in seconds as "3d 4h" / "4h 12m" / "12m". */
export function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * An SVG polyline `points` string for a sparkline of fractions (0..1) in a `width`×`height` box.
 * Empty history yields "". y is flipped so 1.0 is at the top. Values are clamped to 0..1.
 */
export function sparklinePoints(history: number[], width: number, height: number): string {
  if (history.length === 0) return "";
  if (history.length === 1) {
    const y = (1 - clamp01(history[0] ?? 0)) * height;
    return `0,${round(y)} ${width},${round(y)}`;
  }
  const step = width / (history.length - 1);
  return history.map((v, i) => `${round(i * step)},${round((1 - clamp01(v)) * height)}`).join(" ");
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
}
function round(v: number): number {
  return Math.round(v * 10) / 10;
}
